import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays, Loader2, Star, MapPin, ArrowUpRight, Sparkles, ArrowLeft,
  Clock, CheckCircle2, XCircle, Video,
} from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: "Pending review", cls: "bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)]", icon: Clock },
  approved: { label: "Approved", cls: "bg-[color:var(--brand-green)]/10 text-[color:var(--brand-green)]", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]", icon: XCircle },
  draft: { label: "Draft", cls: "bg-[color:var(--soft)] text-[color:var(--ink)]", icon: Clock },
  archived: { label: "Archived", cls: "bg-[color:var(--soft)] text-[color:var(--ink)]", icon: Clock },
};

function formatShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function EventItem({ e, showStatus }) {
  const s = STATUS[e.status] || STATUS.pending;
  const StatusIcon = s.icon;
  return (
    <Link
      to={`/app/events/${e.id}`}
      data-testid={`my-event-${e.id}`}
      className="hub-card group flex items-stretch gap-4 overflow-hidden rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.06)]"
    >
      <div className="relative flex h-full w-32 shrink-0 items-center justify-center overflow-hidden bg-[color:var(--soft)] sm:w-40">
        {e.image ? (
          <img src={e.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <CalendarDays className="h-6 w-6 text-[color:var(--ink-soft)]/40" />
        )}
        {e.featured && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-[color:var(--invert-bg)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white">
            <Star className="h-2.5 w-2.5" /> Featured
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4 pr-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">
            {e.event_type} · {e.format}
          </p>
          {showStatus && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${s.cls}`} data-testid={`my-event-status-${e.id}`}>
              <StatusIcon className="h-3 w-3" /> {s.label}
            </span>
          )}
        </div>
        <h3 className="font-display mt-1 line-clamp-2 text-base font-semibold text-[color:var(--ink)]">{e.title}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[color:var(--ink-soft)]">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {formatShort(e.start_datetime)}</span>
          {(e.city || e.country) && (
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[e.city, e.country].filter(Boolean).join(", ")}</span>
          )}
          {e.format === "Online" && <span className="inline-flex items-center gap-1"><Video className="h-3 w-3" /> Online</span>}
        </div>
        <div className="mt-auto flex items-center justify-end pt-3">
          <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

function EmptyBlock({ title, sub, ctaLabel, ctaTo }) {
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-[color:var(--hairline)] bg-[color:var(--surface)] p-10 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--soft)] text-[color:var(--ink-soft)]">
        <CalendarDays className="h-5 w-5" />
      </span>
      <h3 className="font-display mt-4 text-lg font-semibold text-[color:var(--ink)]">{title}</h3>
      <p className="mt-2 text-sm text-[color:var(--ink-soft)]">{sub}</p>
      {ctaTo && (
        <Link to={ctaTo} className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
          {ctaLabel} <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export default function MyEvents() {
  const [tab, setTab] = useState("interested");
  const [data, setData] = useState({ submitted: [], interested: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hub/events/mine`, { withCredentials: true });
        setData({ submitted: data.submitted || [], interested: data.interested || [] });
      } catch { toast.error("Failed to load your events."); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <AppLayout>
      <Seo title="My Events" description="Events you're interested in and events you've submitted." path="/app/my-events" />
      <div className="container-hub py-8 lg:py-10" data-testid="my-events-page">
        <Link to="/app/events" className="mb-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
          <ArrowLeft className="h-3.5 w-3.5" /> Events
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-teal)]">
              <Sparkles className="h-3.5 w-3.5" /> My Events
            </p>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">
              Events you care about.
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[color:var(--ink-soft)]">
              Everything you've marked interested in and every event you've submitted — with live status.
            </p>
          </div>
          <Link
            to="/app/events/create"
            data-testid="my-events-create-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" /> Publish new event
          </Link>
        </div>

        <div className="mt-8 inline-flex rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] p-1" data-testid="my-events-tabs">
          <button onClick={() => setTab("interested")} data-testid="my-events-tab-interested"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "interested" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"}`}>
            <Star className="h-4 w-4" /> Interested
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === "interested" ? "bg-white text-[color:var(--invert-bg)]" : "bg-[color:var(--soft)] text-[color:var(--ink)]"}`}>{data.interested.length}</span>
          </button>
          <button onClick={() => setTab("submitted")} data-testid="my-events-tab-submitted"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "submitted" ? "bg-[color:var(--invert-bg)] text-white" : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"}`}>
            <CalendarDays className="h-4 w-4" /> Submitted
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === "submitted" ? "bg-white text-[color:var(--invert-bg)]" : "bg-[color:var(--soft)] text-[color:var(--ink)]"}`}>{data.submitted.length}</span>
          </button>
        </div>

        {loading ? (
          <div className="mt-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
        ) : tab === "interested" ? (
          data.interested.length === 0 ? (
            <EmptyBlock
              title="No events on your list yet."
              sub="When you mark an event as interested, it'll be saved here so you don't lose track."
              ctaLabel="Explore events"
              ctaTo="/app/events"
            />
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="my-events-interested-list">
              {data.interested.map((e) => <EventItem key={e.id} e={e} />)}
            </div>
          )
        ) : (
          data.submitted.length === 0 ? (
            <EmptyBlock
              title="You haven't submitted an event yet."
              sub="Standard and Premium members can publish professional events to the whole Hub."
              ctaLabel="Publish an event"
              ctaTo="/app/events/create"
            />
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="my-events-submitted-list">
              {data.submitted.map((e) => <EventItem key={e.id} e={e} showStatus />)}
            </div>
          )
        )}
      </div>
    </AppLayout>
  );
}
